import React, { useState, useEffect, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { Modal, Paper, Fade, IconButton, Box, TextField, InputAdornment, Tooltip, Button } from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, Close as CloseIcon } from "@mui/icons-material";

import axios from "axios";
import { URL } from "../config";
import toast from "react-hot-toast";
import AddStories from "./AddStories";

const StoriesList = () => {

  const [dataSource, setDataSource] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [editStory, setEditStory] = useState(null);

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${URL}/stories`);
      setDataSource(res.data.data || []);
    } catch (err) {
      toast.error("Failed to fetch stories");
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleEdit = (story) => {
    setEditStory(story);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this story?")) return;
    try {
      await axios.delete(`${URL}/stories/${id}`);
      toast.success("Story deleted successfully!");
      fetchAll();
    } catch {
      toast.error("Failed to delete story");
    }
  };

  const columns = useMemo(() => [
    {
      accessorKey: "actions",
      header: "Actions",
      size: 100,
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton size="small" onClick={() => handleEdit(row.original)}>
            <EditIcon sx={{ color: "#174253" }} fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(row.original.storiesId)}>
            <DeleteIcon sx={{ color: "#d32f2f" }} fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    { accessorKey: "storiesId", header: "ID", size: 60 },
    { accessorKey: "storiesTitle", header: "Story Title", size: 200 },
    {
      accessorKey: "storiesVideo",
      header: "Video",
      size: 200,
      Cell: ({ row }) =>
        row.original.storiesVideo ? (
          <video
            src={`${URL}${row.original.storiesVideo}`}
            width="70"
            height="70"
            controls
            style={{ borderRadius: 8, border: "1px solid #ccc" }}
          />
        ) : (
          <span style={{ color: "#888" }}>No Video</span>
        ),
    },
  ], []);

  return (
    <Box sx={{ width: "100%", bgcolor: "#ffffff", marginTop: 2, p: { xs: 1, md: 0 }, overflow: "hidden" }}>

      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, pr: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", fontWeight: 700, fontSize: 22, color: "#174253", letterSpacing: 0.5, borderLeft: "5px solid #174253", pl: 1.5 }}>
          Stories List
        </Box>

        <Button
          sx={{
            borderRadius: 25,
            background: "linear-gradient(90deg, #0b8793, #174253)",
            color: "white",
            "&:hover": { background: "linear-gradient(90deg, #174253, #0b8793)" },
          }}
          onClick={() => {
            setEditStory(null);
            setModalVisible(true);
          }}
        >
          Add Stories
        </Button>
      </Box>

      <MaterialReactTable
        columns={columns}
        data={
          [...dataSource] 
            .reverse()  
            .filter(item =>
              Object.values(item)
                .join(" ")
                .toLowerCase()
                .includes(globalFilter.toLowerCase())
            )
        }
      
        enableColumnActions={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableColumnFilters={false}
        enableHiding={false}
        enableGlobalFilter={false}
      
        muiTablePaperProps={{
          sx: { boxShadow: "none", background: "#fff", borderRadius: "15px" },
        }}
      
        muiTableContainerProps={{
          sx: {
            backgroundColor: "#fff",
            width: "100%",
            overflowX: "auto",
            borderRadius: "12px",
            "&::-webkit-scrollbar": { height: "6px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#e0e3ea",
              borderRadius: "10px",
            },
          },
        }}
      
        muiTableHeadCellProps={{
          sx: {
            backgroundColor: "#e0e0e0ff",
            color: "#393e4f",
            fontWeight: 600,
            fontSize: "16px",
            borderBottom: "0.5px solid #e3e5eb",
            borderRight: "none",
            borderLeft: "none",
            textAlign: "left",
            height: "63px",
            paddingTop: "23px",
            marginTop: "15px",
          },
        }}
      
        muiTableBodyRowProps={{
          sx: {
            backgroundColor: "#fff",
            "&:hover": { backgroundColor: "#eff6fe" },
            "& td": {
              fontSize: { xs: "12px", sm: "14px" },
              p: "8px",
              borderBottom: "0.5px solid #e3e5eb",
              borderRight: "none",
              borderLeft: "none",
              height: "54px",
            },
          },
        }}
      
        initialState={{
          pagination: {
            pageIndex: 0,
            pageSize: 100, 
          },
        }}
      />

      <Modal
        open={modalVisible}
        onClose={() => setModalVisible(false)}
        closeAfterTransition
        BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.6)" } }}
      >
        <Fade in={modalVisible}>
          <Paper sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            p: 0,
            m: 0,
            borderRadius: 0,
            boxShadow: "none",
            overflowY: "auto",
            outline: "none",
            bgcolor: "transparent",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}>
            <AddStories
              story={editStory}
              onClose={() => setModalVisible(false)}
              refresh={fetchAll}
            />
          </Paper>
        </Fade>
      </Modal>
    </Box>
  );
};

export default StoriesList;